/**
 * ConvertLeadDialog — converts a lead to a student.
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Input, Dropdown } from '@/components/common';
import { useConvertLead, type Lead, type ConvertLeadPayload } from '../api/leads';

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing.md};
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const SectionTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 20px 0 12px;
  padding-bottom: 6px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.lg};
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const GENDER_OPTIONS = [
  { value: '', label: 'Select gender…' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];

const RELATIONSHIP_OPTIONS = [
  { value: '', label: 'Select relationship…' },
  { value: 'mother', label: 'Mother' },
  { value: 'father', label: 'Father' },
  { value: 'guardian', label: 'Guardian' },
  { value: 'other', label: 'Other' },
];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  lead: Lead;
}

export const ConvertLeadDialog: React.FC<Props> = ({ isOpen, onClose, lead }) => {
  const navigate = useNavigate();
  const convertMutation = useConvertLead();

  const [form, setForm] = useState<ConvertLeadPayload>({
    date_of_birth: null,
    gender: null,
    guardian_first_name: null,
    guardian_last_name: null,
    guardian_phone: null,
    guardian_email: null,
    guardian_relationship: null,
    notes: null,
  });

  const handleSubmit = async () => {
    const result = await convertMutation.mutateAsync({ id: lead.id, payload: form });
    onClose();
    navigate(`/students/${result.student_id}`);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Convert ${lead.first_name} ${lead.last_name} to Student`}
      size="lg"
      data-testid="convert-lead-dialog"
    >
      <p style={{ fontSize: 14, color: '#64748b', marginBottom: 16 }}>
        This will create a new student record using the lead's information. Fill in any additional details below.
      </p>

      <SectionTitle>Student Info</SectionTitle>
      <FormGrid>
        <Input
          label="Date of Birth"
          type="date"
          value={form.date_of_birth ?? ''}
          onChange={(e) => setForm((f) => ({ ...f, date_of_birth: e.target.value || null }))}
        />
        <Dropdown
          label="Gender"
          options={GENDER_OPTIONS}
          value={form.gender ?? ''}
          onChange={(v) => setForm((f) => ({ ...f, gender: v || null }))}
          placeholder="Select gender…"
        />
      </FormGrid>
      <div style={{ marginTop: 12 }}>
        <Input
          label="Notes"
          value={form.notes ?? ''}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value || null }))}
          placeholder="Additional notes for the student record…"
        />
      </div>

      <SectionTitle>Guardian (optional)</SectionTitle>
      <FormGrid>
        <Input
          label="Guardian First Name"
          value={form.guardian_first_name ?? ''}
          onChange={(e) => setForm((f) => ({ ...f, guardian_first_name: e.target.value || null }))}
        />
        <Input
          label="Guardian Last Name"
          value={form.guardian_last_name ?? ''}
          onChange={(e) => setForm((f) => ({ ...f, guardian_last_name: e.target.value || null }))}
        />
        <Input
          label="Guardian Phone"
          type="tel"
          value={form.guardian_phone ?? ''}
          onChange={(e) => setForm((f) => ({ ...f, guardian_phone: e.target.value || null }))}
        />
        <Input
          label="Guardian Email"
          type="email"
          value={form.guardian_email ?? ''}
          onChange={(e) => setForm((f) => ({ ...f, guardian_email: e.target.value || null }))}
        />
        <Dropdown
          label="Relationship"
          options={RELATIONSHIP_OPTIONS}
          value={form.guardian_relationship ?? ''}
          onChange={(v) => setForm((f) => ({ ...f, guardian_relationship: v || null }))}
          placeholder="Select relationship…"
        />
      </FormGrid>

      {convertMutation.isError && (
        <p style={{ color: '#ef4444', fontSize: 13, marginTop: 12 }}>
          Conversion failed. Please try again.
        </p>
      )}

      <ModalActions>
        <Button variant="outline" onClick={onClose} type="button">
          Cancel
        </Button>
        <Button
          data-testid="convert-submit-btn"
          onClick={handleSubmit}
          disabled={convertMutation.isPending}
        >
          {convertMutation.isPending ? 'Converting…' : 'Convert to Student'}
        </Button>
      </ModalActions>
    </Modal>
  );
};
